import mapValues from 'lodash/mapValues';
import pick from 'lodash/pick';
import { Ref, WatchStopHandle } from 'vue';

export default function useMoveAlongLayer(mapInstance: LooseObject, layerInfo: Ref<MapLayer<MoveAlongLayerConfig>>) {
  if (!layerInfo.value || !layerInfo.value.config) return;

  const { id: layerId } = layerInfo.value;

  let markerStyle; let polylineStyle;
  let unwatchList: WatchStopHandle[] | null = [];

  const markerLayer = new TMap.MultiMarker({
    id: layerId,
    map: mapInstance,
  });

  const polylineLayer = new TMap.MultiPolyline({
    map: mapInstance,
  });

  unwatchList.push(watch(() => pick(
    layerInfo.value.config,
    ['src', 'width', 'height', 'rotate', 'opacity', 'anchor'],
  ), (newValue) => {
    const styleOption = mapValues(newValue, val => toRaw(unref(val)));
    if (!styleOption.src) delete styleOption.src; // 未传src时去掉，避免GL报错
    markerStyle = new TMap.MarkerStyle({
      faceTo: 'map',
      ...styleOption,
    });
    if (markerLayer) markerLayer.setStyles({ markerStyle });
  }, { deep: true, immediate: true }));

  unwatchList.push(watchEffect(() => {
    if (!layerInfo.value.config.latitude || !layerInfo.value.config.longitude) return;
    try {
      const { latitude, longitude, id } = layerInfo.value.config;
      const markerId = String(id || layerId);
      if (markerLayer) {
        markerLayer.setGeometries([{
          id: markerId,
          position: new TMap.LatLng(latitude, longitude),
          styleId: 'markerStyle',
        }]);

        if (layerInfo.value.config.mode === 'start') {
          markerLayer?.moveAlong(
            {
              [markerId]: {
                path: layerInfo.value.config.path.map(item => new TMap.LatLng(item.latitude, item.longitude)),
                duration: layerInfo.value.config.duration,
              },
            },
            {
              autoRotation: layerInfo.value.config.autoRotate,
            },
          );
        } else if (!layerInfo.value.config.mode) {
          markerLayer?.stopMove();
          if (markerLayer?.getGeometryById(markerId)) {
            markerLayer?.updateGeometries([{
              id: markerId,
              styleId: 'markerStyle',
              position: new TMap.LatLng(layerInfo.value.config.latitude, layerInfo.value.config.longitude),
            }]);
          }
        }
      }
    } catch (error) {
      if (markerLayer) markerLayer.setGeometries([]);
    }
  }));

  unwatchList.push(watchEffect(() => {
    const styleConfig = pick(layerInfo.value.config, ['polylineColor', 'polylineWidth', 'polylineBorderWidth',
      'polylineBorderColor']);
    const styleOption = mapValues(styleConfig, val => toRaw(unref(val)));
    polylineStyle = new TMap.PolylineStyle({
      color: styleOption.polylineColor,
      width: styleOption.polylineWidth,
      borderWidth: styleOption.polylineBorderWidth,
      borderColor: styleOption.polylineBorderColor,
    });
    if (polylineLayer) polylineLayer.setStyles({ polylineStyle });
  }));

  unwatchList.push(watchEffect(() => {
    if (!Array.isArray(layerInfo.value.config.path)) return;
    try {
      if (polylineLayer) polylineLayer.setGeometries([{
        styleId: 'polylineStyle',
        // eslint-disable-next-line max-len
        paths: layerInfo.value.config.path.map((item: {latitude: number;longitude: number}) => new TMap.LatLng(item.latitude, item.longitude)),
      }]);
    } catch (error) {
      polylineLayer?.setGeometries([]);
    }
  }));

  return {
    unmount: (): void => {
      markerLayer?.destroy();
      polylineLayer?.destroy();
      unwatchList?.forEach(unwatch => unwatch());
      unwatchList = null;
    },
  };
}
